// Permisos RBAC de Control MDM. Están ENCENDIDOS: los endpoints del backoffice los exigen, así que
// la pantalla se esconde hasta que el rol los tenga sembrados (fail-closed).

/**
 * Claves de permiso del dominio MDM. Van como `string` y no como `Permission` porque
 * `PermissionKey` (generado del OpenAPI) hoy no expone ninguna clave de dominio.
 */
export const MDM_PERMISSIONS = {
    viewFleet: 'mdm.device.status.view',
    refreshStatus: 'mdm.device.status.refresh',
    lockManual: 'mdm.device.lock.manual',
    unlockManual: 'mdm.device.unlock.manual',
    pinUnlock: 'mdm.device.pin.unlock',
    extendValidity: 'mdm.device.validity.extend',
    notify: 'mdm.device.notify',
    release: 'mdm.device.release',
    viewOperationLog: 'mdm.operation.log.view',
} as const;

export type MdmPermissionKey = (typeof MDM_PERMISSIONS)[keyof typeof MDM_PERMISSIONS];

/**
 * El gate está encendido. Hasta que las claves estén sembradas —en el catálogo de la aplicación Y
 * en el rol— lo que protegen queda oculto para todos: es lo correcto, no un bug de front.
 */
export const MDM_PERMISSIONS_ENFORCED = true;

/** Por qué la ficha apaga una acción cuya clave SÍ existe en algún rol. */
export const NO_PERMISSION_LOCK = 'No tienes el permiso para bloquear equipos.';
export const NO_PERMISSION_UNLOCK = 'No tienes el permiso para desbloquear equipos.';
export const NO_PERMISSION_PIN_UNLOCK = 'No tienes el permiso para pedir el PIN de desbloqueo.';
export const NO_PERMISSION_REFRESH = 'No tienes el permiso para consultar al proveedor.';

export interface MdmAbilities {
    canViewFleet: boolean;
    /** Le pregunta al proveedor cómo ve al equipo. No lo cambia: es la única lectura fresca. */
    canRefresh: boolean;
    canLock: boolean;
    canUnlock: boolean;
    canPinUnlock: boolean;
    canExtendValidity: boolean;
    /** Manda el aviso en pantalla. No bloquea ni desbloquea nada. */
    canNotify: boolean;
    /** Saca el equipo del MDM de forma definitiva; es IRREVERSIBLE. */
    canRelease: boolean;
    canViewOperationLog: boolean;
}

/** Con `enforce: false` todo queda permitido; con `true` cada acción exige su clave. */
export function resolveMdmAbilities(
    grantedPermissions: ReadonlySet<string>,
    enforce: boolean = MDM_PERMISSIONS_ENFORCED,
): MdmAbilities {
    if (!enforce) {
        return {
            canViewFleet: true,
            canRefresh: true,
            canLock: true,
            canUnlock: true,
            canPinUnlock: true,
            canExtendValidity: true,
            canNotify: true,
            canRelease: true,
            canViewOperationLog: true,
        };
    }
    return {
        canViewFleet: grantedPermissions.has(MDM_PERMISSIONS.viewFleet),
        canRefresh: grantedPermissions.has(MDM_PERMISSIONS.refreshStatus),
        canLock: grantedPermissions.has(MDM_PERMISSIONS.lockManual),
        canUnlock: grantedPermissions.has(MDM_PERMISSIONS.unlockManual),
        canPinUnlock: grantedPermissions.has(MDM_PERMISSIONS.pinUnlock),
        canExtendValidity: grantedPermissions.has(MDM_PERMISSIONS.extendValidity),
        canNotify: grantedPermissions.has(MDM_PERMISSIONS.notify),
        canRelease: grantedPermissions.has(MDM_PERMISSIONS.release),
        canViewOperationLog: grantedPermissions.has(MDM_PERMISSIONS.viewOperationLog),
    };
}
